import api from "./api";
import {
  ChartData,
  GenerateReportPayload,
  NetworkGraphData,
  PlatformOverviewResponse,
  ReportOption,
  StatusData,
} from "../types";

async function crescimentoUsers(
  professional_id: string | number,
  days?: number,
): Promise<ChartData[]> {
  try {
    const { data } = await api.get(`/reports/users/${professional_id}/`, {
      params: { days },
    });
    return data;
  } catch (e) {
    return [];
  }
}

async function workoutsGrow(
  professional_id: string | number,
  days?: number,
): Promise<ChartData[]> {
  try {
    const { data } = await api.get(`/reports/workouts/${professional_id}/`, {
      params: { days },
    });
    return data;
  } catch (e) {
    return [];
  }
}

async function getDashAulas(
  professional_id: string | number,
): Promise<ChartData[]> {
  try {
    const { data } = await api.get(`/appointments/reports/${professional_id}/`);
    return data;
  } catch (e) {
    return [];
  }
}

async function getReports(): Promise<ReportOption[]> {
  try {
    const { data } = await api.get(`/user-log`);
    return data;
  } catch (e) {
    return [];
  }
}

async function genReport(form: GenerateReportPayload): Promise<unknown[]> {
  try {
    const { data } = await api.post(`/generate-report`, form);
    return data;
  } catch (e) {
    return [];
  }
}

async function getStudentStatus(
  professional_id: string | number,
): Promise<StatusData[]> {
  try {
    const { data } = await api.get(
      `/reports/students-status/${professional_id}/`,
    );
    return data;
  } catch (e) {
    return [];
  }
}

async function getAppointmentStats(
  professional_id: string | number,
  days?: number,
): Promise<StatusData[]> {
  try {
    const { data } = await api.get(
      `/reports/appointments-status/${professional_id}/`,
      { params: { days } },
    );
    return data;
  } catch (e) {
    return [];
  }
}

async function getAvaliationStats(
  professional_id: string | number,
  days?: number,
): Promise<{ count: number }> {
  try {
    const { data } = await api.get(
      `/reports/avaliations-count/${professional_id}/`,
      { params: { days } },
    );
    return data;
  } catch (e) {
    return { count: 0 };
  }
}

async function getExpiredWorkouts(
  professional_id: string | number,
): Promise<{ expired_count: number }> {
  try {
    const { data } = await api.get(
      `/reports/expired-workouts/${professional_id}/`,
    );
    return data;
  } catch (e) {
    return { expired_count: 0 };
  }
}

async function getLinkStats(
  professional_id: string | number,
): Promise<{ label: string; clicks: string }[]> {
  try {
    const { data } = await api.get(`/reports/link-stats/${professional_id}/`);
    return data;
  } catch (e) {
    return [];
  }
}

async function getPlatformOverview(
  days?: number,
  page?: number,
  page_size?: number,
): Promise<PlatformOverviewResponse> {
  try {
    const params: { days?: number; page?: number; page_size?: number } = {};
    if (typeof days === "number") params.days = days;
    if (typeof page === "number") params.page = page;
    if (typeof page_size === "number") params.page_size = page_size;

    const { data } = await api.get(`/reports/platform-overview/`, {
      params,
    });
    return data;
  } catch (e) {
    return {
      summary: {
        total_users: 0,
        professionals: 0,
        students: 0,
        workout_plans: 0,
        appointments: 0,
        avaliations: 0,
        exercises: 0,
        plans: 0,
        advertisements: 0,
      },
      advertisements: {
        total_ads: 0,
        total_views: 0,
        total_clicks: 0,
      },
      feature_usage: {
        total_workouts: 0,
        active_students: 0,
        engaged_students: 0,
        total_food_logs: 0,
        total_user_events: 0,
      },
      professionals_growth: [],
      students_growth: [],
      top_exercises: [],
      users: {
        count: 0,
        next: null,
        previous: null,
        results: [],
      },
    };
  }
}

interface WorkoutStats {
  total_workouts: number;
  frequency_by_day: ChartData[];
  workout_plan_stats: {
    plan_id: number;
    plan_name: string;
    count: number;
  }[];
  top_exercises: {
    exercise_id: string;
    exercise_name: string;
    count: number;
  }[];
  weekday_stats: {
    day: string;
    count: number;
  }[];
  adherence_rate: number;
  days_analyzed: number;
  days_with_workout: number;
  word_cloud_image_url?: string;
}

async function getStudentWorkoutStats(
  student_id: string | number,
  professional_id?: string | number,
  days?: number,
): Promise<WorkoutStats> {
  try {
    const params: { professional_id?: string | number; days?: number } = {};
    if (professional_id) params.professional_id = professional_id;
    if (days) params.days = days;

    const { data } = await api.get(`/tracking/student-stats/${student_id}/`, {
      params,
    });
    return data;
  } catch (e) {
    console.error("Erro ao buscar estatísticas de treino:", e);
    return {
      total_workouts: 0,
      frequency_by_day: [],
      workout_plan_stats: [],
      top_exercises: [],
      weekday_stats: [],
      adherence_rate: 0,
      days_analyzed: days || 30,
      days_with_workout: 0,
      word_cloud_image_url: "",
    };
  }
}

interface FoodLogStats {
  total_meals: number;
  total_calories: number;
  frequency_by_day: {
    date: string;
    calories: number;
    count: number;
  }[];
  meal_type_stats: {
    meal_type: string;
    meal_name: string;
    count: number;
    total_calories: number;
  }[];
  weekday_stats: {
    day: string;
    count: number;
    calories: number;
  }[];
  adherence_rate: number;
  days_analyzed: number;
  days_with_meals: number;
  avg_daily_calories: number;
  avg_daily_meals: number;
  word_cloud_image_url?: string;
  // Dados nutricionais agregados do TACO
  nutrition_summary?: {
    total_proteina_g: number;
    total_carboidrato_g: number;
    total_lipideos_g: number;
    total_fibra_g: number;
    avg_proteina_g: number;
    avg_carboidrato_g: number;
    avg_lipideos_g: number;
    avg_fibra_g: number;
    distribuicao_macros: {
      proteina_pct: number;
      carboidrato_pct: number;
      lipideos_pct: number;
    };
    meals_with_taco_data: number;
    meals_without_taco_data: number;
  };
}

async function getStudentFoodLogStats(
  student_id: string | number,
  days?: number,
): Promise<FoodLogStats> {
  try {
    const params: { days?: number } = {};
    if (days) params.days = days;

    const { data } = await api.get(`/food-logs/student-stats/${student_id}/`, {
      params,
    });
    return data;
  } catch (e) {
    console.error("Erro ao buscar estatísticas de alimentação:", e);
    return {
      total_meals: 0,
      total_calories: 0,
      frequency_by_day: [],
      meal_type_stats: [],
      weekday_stats: [],
      adherence_rate: 0,
      days_analyzed: days || 30,
      days_with_meals: 0,
      avg_daily_calories: 0,
      avg_daily_meals: 0,
      word_cloud_image_url: "",
    };
  }
}

interface MealTypeMacros {
  proteina_g: number;
  carboidrato_g: number;
  lipideos_g: number;
  fibra_alimentar_g: number;
}

interface MealTypeItem {
  name: string;
  quantity: string;
  quantities: string[];
  total_calories: number;
  occurrences: number;
  macros: MealTypeMacros;
  nome_taco: string | null;
}

interface MealTypeMacrosTotais extends MealTypeMacros {
  energia_kcal: number;
}

interface MealTypeDetails {
  meal_type: string;
  total_items: number;
  total_occurrences: number;
  items: MealTypeItem[];
  macros_totais: MealTypeMacrosTotais;
}

async function getMealTypeDetails(
  student_id: string | number,
  meal_type: string,
  days?: number,
): Promise<MealTypeDetails> {
  try {
    const params: { days?: number } = {};
    if (days) params.days = days;

    const { data } = await api.get(
      `/food-logs/meal-type-details/${student_id}/`,
      {
        params: { ...params, meal_type },
      },
    );
    return data;
  } catch (e) {
    console.error("Erro ao buscar detalhes do tipo de refeição:", e);
    return {
      meal_type,
      total_items: 0,
      total_occurrences: 0,
      items: [],
      macros_totais: {
        proteina_g: 0,
        carboidrato_g: 0,
        lipideos_g: 0,
        fibra_alimentar_g: 0,
        energia_kcal: 0,
      },
    };
  }
}

interface ExerciseLog {
  id: number;
  exercise_id: string;
  exercise_name: string;
  workout_plan_id: number | null;
  workout_plan_name: string;
  completed: boolean;
  date: string;
  date_formatted: string;
}

interface ExerciseLogsResponse {
  exercise_id: string;
  exercise_name: string;
  total_executions: number;
  logs: ExerciseLog[];
}

async function getExerciseLogs(
  student_id: string | number,
  exercise_id: string,
  professional_id?: string | number,
  days?: number,
): Promise<ExerciseLogsResponse> {
  try {
    const params: { professional_id?: string | number; days?: number } = {};
    if (professional_id) params.professional_id = professional_id;
    if (days) params.days = days;

    const { data } = await api.get(
      `/tracking/exercise-logs/${student_id}/${exercise_id}/`,
      {
        params,
      },
    );
    return data;
  } catch (e) {
    console.error("Erro ao buscar logs do exercício:", e);
    return {
      exercise_id,
      exercise_name: "",
      total_executions: 0,
      logs: [],
    };
  }
}

async function getNetworkGraph(days?: number): Promise<NetworkGraphData> {
  try {
    const params: { days?: number } = {};
    if (typeof days === "number") params.days = days;

    const { data } = await api.get(`/reports/network-graph/`, { params });
    return data;
  } catch (e) {
    console.error("Erro ao buscar network graph:", e);
    return {
      nodes: [],
      edges: [],
    };
  }
}

interface ActivityVsNutritionComparison {
  days_analyzed: number;
  summary: {
    total_workouts: number;
    total_meals: number;
    total_calories: number;
    workout_adherence: number;
    nutrition_adherence: number;
    consistency_rate: number;
    balance_score: number;
  };
  tracking_overlap: {
    days_with_both: number;
    days_with_only_workout: number;
    days_with_only_nutrition: number;
    days_with_neither: number;
    overlap_percentage: number;
  };
  daily_comparison: Array<{
    date: string;
    workouts: number;
    meals: number;
    calories: number;
    has_activity: boolean;
    has_nutrition: boolean;
    both_tracked: boolean;
  }>;
  weekday_comparison: Array<{
    day: string;
    avg_workouts: number;
    avg_meals: number;
    avg_calories: number;
    total_workouts: number;
    total_meals: number;
  }>;
  correlations: {
    avg_workouts_when_both: number;
    avg_calories_when_both: number;
    avg_calories_with_workout: number;
    avg_calories_without_workout: number;
    calorie_difference: number;
  };
  nutrition_summary?: {
    total_proteina_g: number;
    total_carboidrato_g: number;
    total_lipideos_g: number;
    total_fibra_g: number;
    avg_proteina_g: number;
    avg_carboidrato_g: number;
    avg_lipideos_g: number;
    avg_fibra_g: number;
    distribuicao_macros: {
      proteina_pct: number;
      carboidrato_pct: number;
      lipideos_pct: number;
    };
    meals_with_taco_data: number;
    meals_without_taco_data: number;
  };
}

async function getActivityVsNutritionComparison(
  student_id: string | number,
  professional_id?: string | number,
  days?: number,
): Promise<ActivityVsNutritionComparison> {
  try {
    const params: { professional_id?: string | number; days?: number } = {};
    if (professional_id) params.professional_id = professional_id;
    if (days) params.days = days;

    const { data } = await api.get(
      `/reports/activity-vs-nutrition/${student_id}/`,
      { params },
    );
    return data;
  } catch (e) {
    console.error("Erro ao buscar comparação atividade vs nutrição:", e);
    return {
      days_analyzed: days || 30,
      summary: {
        total_workouts: 0,
        total_meals: 0,
        total_calories: 0,
        workout_adherence: 0,
        nutrition_adherence: 0,
        consistency_rate: 0,
        balance_score: 0,
      },
      tracking_overlap: {
        days_with_both: 0,
        days_with_only_workout: 0,
        days_with_only_nutrition: 0,
        days_with_neither: days || 30,
        overlap_percentage: 0,
      },
      daily_comparison: [],
      weekday_comparison: [],
      correlations: {
        avg_workouts_when_both: 0,
        avg_calories_when_both: 0,
        avg_calories_with_workout: 0,
        avg_calories_without_workout: 0,
        calorie_difference: 0,
      },
    };
  }
}

const reports = {
  crescimentoUsers,
  workoutsGrow,
  getDashAulas,
  getReports,
  genReport,
  getStudentStatus,
  getAppointmentStats,
  getAvaliationStats,
  getExpiredWorkouts,
  getLinkStats,
  getStudentWorkoutStats,
  getStudentFoodLogStats,
  getMealTypeDetails,
  getExerciseLogs,
  getPlatformOverview,
  getNetworkGraph,
  getActivityVsNutritionComparison,
};

export default reports;
